import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

/**
 * A custom element that manages free gift variants in the cart based on SaveMec quantity.
 * This component is now controlled directly by other components (like cart-items) instead of listening to events.
 */
class GiftManagerComponent extends Component {
  /** @type {boolean} */
  #isProcessing = false;

  get #mainHandle()      { return this.dataset.mainHandle || 'savemec'; }
  get #gift1Id()         { return this.dataset.gift1Id || ''; }
  get #gift1Threshold()  { return parseInt(this.dataset.gift1Threshold || '0') || 2; }
  get #gift2Id()         { return this.dataset.gift2Id || ''; }
  get #gift2Threshold()  { return parseInt(this.dataset.gift2Threshold || '0') || 3; }
  get #gift3Id()         { return this.dataset.gift3Id || ''; }
  get #gift3Threshold()  { return parseInt(this.dataset.gift3Threshold || '0') || 4; }
  get #doubleThreshold() { return parseInt(this.dataset.giftDoubleThreshold || '0') || 6; }

  connectedCallback() {
    super.connectedCallback();
    // On initial load, check the cart state once.
    fetch('/cart.js')
      .then(res => res.ok ? res.json() : Promise.reject('Failed to fetch initial cart'))
      .then(this.checkAndManageGifts)
      .catch(error => console.debug('GiftManager: Initial cart fetch failed.', error));
  }

  /**
   * PUBLIC METHOD: Main logic to check and manage gifts.
   * Can be called directly by other components.
   * @param {any} cartData - A full Shopify cart object.
   */
  checkAndManageGifts = async (cartData) => {
    // Prevent concurrent executions & exit if no valid cart data is provided.
    if (this.#isProcessing || !cartData || !Array.isArray(cartData.items)) {
      console.debug('GiftManager: Check skipped - processing or invalid cart data.');
      return;
    }

    this.#isProcessing = true;

    try {
      const cart = cartData;
      
      const mainHandle = this.#mainHandle;
      let saveMecQuantity = 0;

      for (const item of cart.items) {
        const productHandle = item.product_handle || item.handle || '';
        if (productHandle.includes(mainHandle) || item.product_title?.toLowerCase().includes(mainHandle)) {
          saveMecQuantity += item.quantity;
        }
      }
      
      const dbl = this.#doubleThreshold;
      const gifts = [
        { id: this.#gift1Id, threshold: this.#gift1Threshold },
        { id: this.#gift2Id, threshold: this.#gift2Threshold },
        { id: this.#gift3Id, threshold: this.#gift3Threshold },
      ];

      /** @type {Record<string, number>} */
      const updates = {};

      for (const gift of gifts) {
        if (!gift.id) continue;
        const target = saveMecQuantity >= dbl ? 2 : saveMecQuantity >= gift.threshold ? 1 : 0;
        const allInstances = cart.items.filter((/** @type {any} */ i) => String(i.variant_id) === gift.id);
        if (allInstances.length === 0) {
          if (target > 0) updates[gift.id] = target;
        } else {
          if (allInstances[0].quantity !== target) updates[allInstances[0].key] = target;
          // Remove duplicate gift line items
          for (let j = 1; j < allInstances.length; j++) updates[allInstances[j].key] = 0;
        }
      }

      // If there are changes, make a single batch request
      if (Object.keys(updates).length > 0) {
        const cartItemsComponents = document.querySelectorAll('cart-items-component');
        const sectionsToUpdate = new Set();
        cartItemsComponents.forEach(item => {
          if (item instanceof HTMLElement && item.dataset.sectionId) {
            sectionsToUpdate.add(item.dataset.sectionId);
          }
        });

        const body = {
          updates: updates,
          sections: Array.from(sectionsToUpdate).join(','),
          sections_url: window.location.pathname
        };

        const res = await fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body)
        });

        if (res.ok) {
          const updatedCart = await res.json();
          // Dispatch a final update event so other theme components can react
          document.documentElement.dispatchEvent(new CustomEvent('cart:update', {
            bubbles: true,
            detail: {
              resource: updatedCart,
              sourceId: 'gift-manager-component',
              data: { sections: updatedCart.sections, itemCount: updatedCart.item_count }
            }
          }));
        } else {
          // If the batch update fails, try to reload the whole cart state to recover
          const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
          cartDrawer?.renderContents(cartData); // Attempt to re-render with old data to avoid broken state
        }
      }
    } catch (error) {
      console.debug('Gift manager check failed:', error);
    } finally {
      this.#isProcessing = false;
    }
  };
}

// Dummy methods that are no longer used by the new batch logic but kept to avoid breaking other potential dependencies
const noOp = () => Promise.resolve();

if (!customElements.get('gift-manager-component')) {
  // Define the main component
  customElements.define('gift-manager-component', GiftManagerComponent);
  // Add placeholder methods to the prototype in case something else tries to call them, preventing errors.
  GiftManagerComponent.prototype.addFreeGift = noOp;
  GiftManagerComponent.prototype.removeItem = noOp;
  GiftManagerComponent.prototype.forceQuantity = noOp;
}
